import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppConfig } from 'src/app/api/appconfig';
import { SigninModel } from 'src/app/_models/user/signin';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';
import { StepService } from 'src/app/_services/step.service.service';
import { UserService } from 'src/app/_services/user.service';
import { ConfigService } from 'src/app/service/app.config.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-signin-v2',
    templateUrl: './signin-v2.component.html',
    styleUrls: ['./signin-v2.component.scss']
})
export class SigninV2Component implements OnInit, AfterViewInit, OnDestroy {
    signinForm: FormGroup;
    forgotpasswordForm: FormGroup;

    config: AppConfig;
    subscription: Subscription;
    private queryParamsSub: Subscription;
    private autofillInterval: any;
    mode: 'login' | 'forgot' = 'login';

    showPassword = false;
    isAutoLogging = false;
    brandName = 'LincSell';
    backgroundImage = 'assets/images/Singup.webp';

    _globals = GlobalService;
    private tabId: string = this.generateTabId();
    private beforeUnloadHandler = () => this.cleanupTab(true);
    @ViewChild('emailField') emailField!: ElementRef<HTMLInputElement>;
    @ViewChild('passwordField') passwordField!: ElementRef<HTMLInputElement>;

    constructor(
        public _configService: ConfigService,
        private _router: Router,
        private _fb: FormBuilder,
        public _userService: UserService,
        private _connMessageService: ConnMessageService,
        private _abd: AppBaseDataService,
        private stepService: StepService,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        const hasSignupData = sessionStorage.getItem('success-redired') !== null;

        /** -----------------------------
         *  UI Spinner + Redirect Styling
         * ----------------------------- */
        if (hasSignupData) {
            this._abd.showSpinner();
        } else {
            document.body.classList.remove('spinner-dark-redirect');
            this._abd.hideSpinner();
        }

        /** -----------------------------
         *  Clear session data if no steps in progress
         * ----------------------------- */
        if (!this.stepService.isInProgress()) {
            sessionStorage.removeItem('personal-Info');
            sessionStorage.removeItem('business-Info');
            sessionStorage.removeItem('payment-successfully');
            sessionStorage.removeItem('success-redired');
        }

        /** -----------------------------
         *  Initialize Global Settings
         * ----------------------------- */
        try {
            const whiteLabel = this._globals.whiteLabel;

            this.brandName = whiteLabel?.wlName || 'LincSell';
            this.backgroundImage =
                whiteLabel?.backgroundImage || 'assets/images/Singup.webp';

            // Apply brand color to root variables
            if (whiteLabel?.brandColor) {
                document.documentElement.style.setProperty('--brandColor', whiteLabel.brandColor);
                document.documentElement.style.setProperty('--bs-primary', whiteLabel.brandColor);
            }

            /** Config subscription */
            this.config = this._configService.config;
            this.subscription = this._configService.configUpdate$.subscribe(cfg => {
                this.config = cfg;
            });

            /** Forms + login detection */
            this.initForms();
            this.prefillEmailFromQuery();
            this.queryParamsSub = this.route.queryParamMap.subscribe(() => {
                this.prefillEmailFromQuery();
            });
            this.detectAutoLogin();

            /** -----------------------------
             *   Payment success handler
             * ----------------------------- */
            const paymentSuccess = sessionStorage.getItem('payment-successfully');
            if (paymentSuccess) {
                // Remove only payment-related data, not everything
                sessionStorage.removeItem('payment-successfully');
            }

            /** Remove signup loader flag */
            if (hasSignupData) {
                sessionStorage.removeItem('success-redired');
            }

        } catch (error) {
            console.warn('signin-v2 init error', error);
        }

        /** -----------------------------
         *  Register Tab + beforeUnload
         * ----------------------------- */
        this.registerTab();
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }


    ngAfterViewInit(): void {
        // Give the browser time to autofill, then sync into the form model so validation enables the button.
        setTimeout(() => this.syncAutofillFromDom(), 120);
        setTimeout(() => this.syncAutofillFromDom(), 400);
        setTimeout(() => this.syncAutofillFromDom(true), 900);

        // Fallback: poll briefly to catch delayed autofill and enable submit
        let attempts = 0;
        this.autofillInterval = setInterval(() => {
            attempts++;
            this.syncAutofillFromDom(true);
            if (this.signinForm.valid || attempts > 10) {
                clearInterval(this.autofillInterval);
            }
        }, 250);

    }

    private initForms(): void {
        this.signinForm = this._fb.group({
            email: ['', [Validators.required, this.emailFormatValidator()]],
            password: ['', Validators.required]
        });

        this.forgotpasswordForm = this._fb.group({
            email: ['', [Validators.required, this.emailFormatValidator()]]
        });
    }

    private prefillEmailFromQuery(): void {
        const emailParam =
            this.route.snapshot.queryParamMap.get('email') ||
            this.getEmailFromHashUrl();
        if (!emailParam) return;

        const trimmed = emailParam.trim();
        if (!this._globals.isValidEmail(trimmed)) return;

        this.signinForm?.patchValue({ email: trimmed }, { emitEvent: false });
        this.forgotpasswordForm?.patchValue({ email: trimmed }, { emitEvent: false });
        this.signinForm?.get('email')?.updateValueAndValidity({ emitEvent: false });
    }

    private getEmailFromHashUrl(): string | null {
        try {
            const href = window.location.href || '';
            const hashIndex = href.indexOf('#');
            const source = hashIndex >= 0 ? href.substring(hashIndex + 1) : href;
            const match = source.match(/[?&]email=([^&#]+)/i);
            if (!match?.[1]) return null;
            return decodeURIComponent(match[1].replace(/\+/g, ' '));
        } catch {
            return null;
        }
    }

    private detectAutoLogin(): void {
        const autoFlag = sessionStorage.getItem('auto-login') === '1';
        if (autoFlag) {
            this.isAutoLogging = true;
            this._abd.showSpinner();
        }
    }

    emailFormatValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;
            const pvalid = value.match(this._globals.emailValidatorString);
            return pvalid ? null : { emailStrength: true };
        };
    }

    ngOnDestroy(): void {
        if (this.autofillInterval) {
            clearInterval(this.autofillInterval);
        }
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.cleanupTab(false);
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.queryParamsSub) {
            this.queryParamsSub.unsubscribe();
        }
    }

    togglePassword(): void {
        this.showPassword = !this.showPassword;
    }

    signin(): void {
        this._globals.loginErrorMessage = '';
        localStorage.removeItem('user');
        this._abd.showSpinner();
        const x = this.signinForm.value;
        const signinPayload: SigninModel = {
            Username: x.email,
            Password: x.password,
            rememberMe: false
        };
        this._userService.login(signinPayload);
    }

    setMode(val: 'login' | 'forgot'): void {
        this.mode = val;
    }

    forgotpasswordSubmit(): void {
        const email = this.forgotpasswordForm.controls.email.value;
        this._abd.showSpinner();
        this._userService.sendForgotpasswordEmail(email).subscribe((res: any) => {
            this._abd.hideSpinner();
            if (res.success && res.data) {
                this._connMessageService.showToastMessage(
                    'Email has been sent on provided email. Please check your email!',
                    'success'
                );
            } else {
                this._connMessageService.showToastMessage(res?.message || 'Unable to send reset email.', 'error');
            }
            this._router.navigate(['pages/signin-v2']);
        }, () => {
            this._abd.hideSpinner();
        });
    }

    openPricing(): void {
        // environment.SignupURL, not a hardcoded host — a hardcoded production URL sent dev and
        // staging users straight to the production signup funnel. An absolute http(s) value is an
        // external hand-off (prod's marketing pricing page); anything else is an in-app route, so
        // dev/staging stay on the origin they're already on.
        const target = environment.SignupURL;
        if (/^https?:\/\//i.test(target)) {
            window.location.href = target;
        } else {
            this._router.navigate([target]);
        }
    }

    private syncAutofillFromDom(markTouched = false): void {
        const emailVal = this.emailField?.nativeElement?.value?.trim();
        const passwordVal = this.passwordField?.nativeElement?.value;

        if (emailVal && this.signinForm.get('email')?.value !== emailVal) {
            this.signinForm.get('email')?.setValue(emailVal);
        }
        if (passwordVal && this.signinForm.get('password')?.value !== passwordVal) {
            this.signinForm.get('password')?.setValue(passwordVal);
        }
        if (markTouched && emailVal && passwordVal) {
            this.signinForm.markAsDirty();
            this.signinForm.markAllAsTouched();
        }
        this.signinForm.updateValueAndValidity();
    }

    private registerTab(): void {
        try {
            const existing = this.getActiveTabs();
            if (!existing.includes(this.tabId)) {
                existing.push(this.tabId);
                localStorage.setItem('signin-active-tabs', JSON.stringify(existing));
            }
        } catch { /* ignore storage errors */ }
    }

    private cleanupTab(clearSession: boolean): void {
        try {
            const existing = this.getActiveTabs().filter(id => id !== this.tabId);
            localStorage.setItem('signin-active-tabs', JSON.stringify(existing));

            // Only clear if this was the last tab
            if (existing.length === 0 && clearSession) {
                localStorage.clear();
                sessionStorage.clear();
                this._userService.logout?.();
            }
        } catch { /* ignore storage errors */ }
    }

    private getActiveTabs(): string[] {
        try {
            const stored = localStorage.getItem('signin-active-tabs');
            const parsed = stored ? JSON.parse(stored) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private generateTabId(): string {
        try {
            // Prefer native randomUUID when available
            if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
                // @ts-ignore
                return crypto.randomUUID();
            }
        } catch { /* ignore */ }
        // Fallback
        return 'tab-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
}
